import React, { useCallback, useEffect, useState } from 'react';
import redditApi from '../../../services/redditApi';
import { useAccountQualityCheck, AccountQualityResult } from '../components/AccountQualityCheck';

const ACCOUNT_STATUS_LABELS = {
  passed: 'Passed',
  failed: 'Failed',
  checking: 'Checking',
  needs_manual_review: 'Needs Review',
  error: 'Error',
  force_added: 'Force Added',
  banned: 'Banned',
};

function LockIcon() {
  return (
    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function Toast({ message, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3500);
    return () => clearTimeout(t);
  }, [onDone]);
  return <div className={`rh-toast rh-toast-${type}`}>{message}</div>;
}

/* ─── Invite Modal ─────────────────────────────────────────────────── */
const ROLES = [
  { id: 'redditor', label: 'Redditor', hint: 'Assigned Reddit comments, tracked via dashboard.' },
  { id: 'accountant', label: 'Accountant', hint: 'Can record & view payments. No comment assignments.' },
  { id: 'admin', label: 'Admin', hint: 'Full access to all campaigns, members, and settings.' },
];

function InviteModal({ onClose, onSent, hasAccountant, availableClients }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('redditor');
  const [rate, setRate] = useState('');
  const [validatedAccounts, setValidatedAccounts] = useState([]); // [{ username, validation_id }]
  const [newUsername, setNewUsername] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const qc = useAccountQualityCheck();

  const availableRoles = hasAccountant
    ? ROLES.filter((r) => r.id !== 'accountant')
    : ROLES;

  function checkAccount() {
    const username = newUsername.trim().replace(/^u\//, '');
    if (!username) return;
    if (validatedAccounts.some((a) => a.username.toLowerCase() === username.toLowerCase())) {
      setError(`u/${username} has already been added.`);
      return;
    }
    setError('');
    qc.validate(username);
  }

  function addValidatedAccount() {
    if (!qc.result || qc.result.status !== 'passed') return;
    setValidatedAccounts((prev) => (
      prev.some((a) => a.username.toLowerCase() === qc.result.username.toLowerCase())
        ? prev
        : [...prev, { username: qc.result.username, validation_id: qc.result.validation_id, forced: false }]
    ));
    setNewUsername('');
    qc.reset();
  }

  function forceAddAccount() {
    if (!qc.result || !['failed', 'needs_manual_review'].includes(qc.result.status)) return;
    setValidatedAccounts((prev) => (
      prev.some((a) => a.username.toLowerCase() === qc.result.username.toLowerCase())
        ? prev
        : [...prev, { username: qc.result.username, validation_id: qc.result.validation_id, forced: true }]
    ));
    setNewUsername('');
    qc.reset();
  }

  function removeValidatedAccount(idx) {
    setValidatedAccounts((prev) => prev.filter((_, i) => i !== idx));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    if (role === 'redditor' && validatedAccounts.length === 0) {
      setError('Add at least one quality-checked Reddit account before sending the invite.');
      return;
    }
    setBusy(true);
    try {
      const payload = { email, role };
      if (role === 'redditor') {
        payload.rate_per_comment = parseFloat(rate) || 0;
        payload.reddit_accounts = validatedAccounts.map((a) => a.username);
        payload.reddit_account_validation_ids = validatedAccounts.map((a) => a.validation_id);
        payload.reddit_account_forced_ids = validatedAccounts
          .filter((a) => a.forced)
          .map((a) => a.validation_id);
      }
      await redditApi.post('/admin/invite/', payload);
      onSent(email);
      onClose();
    } catch (err) {
      const d = err.response?.data;
      setError(d?.detail || d?.email?.[0] || 'Failed to send invite.');
    } finally {
      setBusy(false);
    }
  }

  const hasPanel = role === 'redditor' && (qc.phase === 'checking' || !!qc.result);

  const selectedIdx = availableRoles.findIndex((r) => r.id === role);
  const count = availableRoles.length;

  return (
    <div className="rh-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="rh-modal"
        style={{
          maxWidth: hasPanel ? 560 : undefined,
          transition: 'max-width 0.25s ease',
        }}
      >
        <h3 className="rh-modal-title">Add Member</h3>
        <p className="rh-modal-subtitle">Send an invite link — they'll set their own name and password.</p>

        {/* Role toggle — 2 or 3 options depending on whether accountant slot is taken */}
        <div className="rh-role-toggle">
          <div
            className="rh-role-toggle-pill"
            style={{ left: `calc(${selectedIdx} * (100% / ${count}))`, width: `calc(100% / ${count})` }}
          />
          {availableRoles.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`rh-role-toggle-btn${role === r.id ? ' rh-role-toggle-btn--active' : ' rh-role-toggle-btn--inactive'}`}
              onClick={() => setRole(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
        <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', margin: '-4px 0 16px', textAlign: 'center' }}>
          {availableRoles.find((r) => r.id === role)?.hint}
        </p>

        {error && <div className="rh-auth-error">{error}</div>}

        <form onSubmit={onSubmit}>
          <div className="rh-form-group">
            <label>Email address</label>
            <input
              type="email"
              placeholder="person@example.com"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--rh-text-muted)' }}>
              They'll receive an invite link to set up their own account.
            </p>
          </div>

          {role === 'redditor' && (
            <>
              <div className="rh-form-group">
                <label>Rate per submitted comment ($)</label>
                <input
                  type="number"
                  placeholder="e.g. 3.00"
                  min="0"
                  step="0.01"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                />
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--rh-text-muted)' }}>
                  USD paid per successfully submitted (non-deleted) comment each month.
                </p>
              </div>

              <div className="rh-form-group">
                <label>Reddit Accounts <span style={{ color: '#dc2626' }}>*</span></label>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--rh-text-muted)' }}>
                  Each account must pass a quality check before it can be added. Only accepted accounts are onboarded.
                </p>

                {validatedAccounts.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    {validatedAccounts.map((a, idx) => (
                      <div
                        key={a.validation_id}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          background: 'var(--rh-bg)', border: '1px solid var(--rh-border)',
                          borderRadius: 8, padding: '9px 14px',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13, color: 'var(--rh-text)' }}>
                            u/{a.username}
                          </span>
                          {a.forced ? (
                            <span className="rh-badge rh-badge-force_added">Force Added</span>
                          ) : (
                            <span className="rh-badge rh-badge-passed">Passed</span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeValidatedAccount(idx)}
                          title="Remove account"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 6, color: 'var(--rh-text-muted)', fontSize: 16, lineHeight: 1 }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    placeholder="username (without u/)"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), checkAccount())}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="rh-btn-primary"
                    style={{ flexShrink: 0 }}
                    onClick={checkAccount}
                    disabled={qc.phase === 'checking' || !newUsername.trim()}
                  >
                    {qc.phase === 'checking' ? 'Checking…' : 'Check'}
                  </button>
                </div>
                {qc.error && <p className="rh-input-error">{qc.error}</p>}

                {/* Quality-check result — stacked below the input */}
                {hasPanel && (
                  <div style={{ marginTop: 12 }}>
                    <AccountQualityResult
                      result={qc.result}
                      onAddAccount={addValidatedAccount}
                      onForceAdd={forceAddAccount}
                      onRetry={qc.reset}
                      addLabel="Add to Invite"
                      forceLabel="Force Add to Invite"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          <div className="rh-modal-actions">
            <button type="button" className="rh-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="rh-btn-primary" disabled={busy}>
              {busy ? 'Sending…' : `Send Invite as ${role.charAt(0).toUpperCase() + role.slice(1)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Accounts Modal ────────────────────────────────────────────────── */
function AccountsModal({ member, onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUsername, setNewUsername] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const qc = useAccountQualityCheck();

  useEffect(() => {
    redditApi.get(`/admin/redditors/${member.id}/accounts/`)
      .then(({ data }) => setAccounts(data))
      .catch(() => setError('Failed to load accounts.'))
      .finally(() => setLoading(false));
  }, [member.id]);

  function addAccount() {
    const username = newUsername.trim().replace(/^u\//, '');
    if (!username) return;
    setError('');
    qc.validate(username);
  }

  async function saveAccount({ force = false } = {}) {
    if (!qc.result) return;
    if (!force && qc.result.status !== 'passed') return;
    if (force && !['failed', 'needs_manual_review'].includes(qc.result.status)) return;
    setAdding(true);
    setError('');
    try {
      const { data } = await redditApi.post(
        `/admin/redditors/${member.id}/accounts/`,
        { validation_id: qc.result.validation_id, force },
      );
      setAccounts((prev) => [...prev, data]);
      setNewUsername('');
      qc.reset();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add account.');
    } finally {
      setAdding(false);
    }
  }

  async function toggleStatus(acct) {
    const usable = acct.validation_status === 'passed' || acct.force_added;
    const next = usable ? 'failed' : 'passed';
    setError('');
    try {
      const { data } = await redditApi.patch(
        `/admin/redditors/${member.id}/accounts/${acct.id}/`,
        { validation_status: next },
      );
      setAccounts((prev) => prev.map((a) => (a.id === acct.id ? data : a)));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update account status.');
    }
  }

  async function removeAccount(acct) {
    const linked = (acct.comment_count || 0) > 0;
    if (linked) {
      setError(
        `u/${acct.username} has ${acct.comment_count} linked comment${acct.comment_count !== 1 ? 's' : ''} and cannot be deleted. Archive it from the redditor detail page instead.`,
      );
      return;
    }
    try {
      await redditApi.delete(`/admin/redditors/${member.id}/accounts/${acct.id}/`);
      setAccounts((prev) => prev.filter((a) => a.id !== acct.id));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove account.');
    }
  }

  const hasPanel = qc.phase === 'checking' || !!qc.result;

  return (
    <div className="rh-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="rh-modal"
        style={{
          maxWidth: hasPanel ? 920 : 460,
          width: hasPanel ? '92vw' : undefined,
          transition: 'max-width 0.25s ease',
        }}
      >
        <h3 className="rh-modal-title">Reddit Accounts</h3>
        <p className="rh-modal-subtitle">{member.full_name} — manage posting accounts</p>

        {error && <div className="rh-auth-error">{error}</div>}

        <div style={{ display: 'flex', gap: hasPanel ? 24 : 0, alignItems: 'flex-start' }}>
          {/* ── Left column: accounts + add/check ── */}
          <div style={{ flex: hasPanel ? '1 1 0' : '1 1 100%', minWidth: 0 }}>
            {/* Account list */}
            <div style={{ margin: '16px 0' }}>
              {loading ? (
                <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', margin: 0 }}>Loading…</p>
              ) : accounts.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', margin: 0 }}>No accounts yet. Add one below.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {accounts.map((a) => (
                    <div
                      key={a.id}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: 'var(--rh-bg)', border: '1px solid var(--rh-border)',
                        borderRadius: 8, padding: '9px 14px',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13, color: 'var(--rh-text)' }}>
                          u/{a.username}
                        </span>
                        {a.is_banned ? (
                          <span
                            className="rh-badge rh-badge-banned"
                            title={a.banned_at ? `Banned ${new Date(a.banned_at).toLocaleString()}` : 'Banned on Reddit'}
                          >
                            Banned
                          </span>
                        ) : a.is_archived ? (
                          <span className="rh-badge rh-badge-archived">Archived</span>
                        ) : a.force_added ? (
                          <span className="rh-badge rh-badge-force_added">Force Added</span>
                        ) : a.validation_status ? (
                          <span className={`rh-badge rh-badge-${a.validation_status}`}>
                            {ACCOUNT_STATUS_LABELS[a.validation_status] || a.validation_status}
                          </span>
                        ) : null}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        {(() => {
                          const usable = a.validation_status === 'passed' || a.force_added;
                          return (
                            <button
                              type="button"
                              onClick={() => toggleStatus(a)}
                              title={usable ? 'Block this account (mark Failed)' : 'Allow this account (mark Passed)'}
                              className="rh-btn-link"
                              style={{ fontSize: 12, padding: '2px 6px' }}
                            >
                              {usable ? 'Mark Failed' : 'Mark Passed'}
                            </button>
                          );
                        })()}
                        <button
                          onClick={() => removeAccount(a)}
                          title={(a.comment_count || 0) > 0
                            ? 'Linked to comments — archive from redditor detail instead'
                            : 'Remove account'}
                          disabled={(a.comment_count || 0) > 0}
                          style={{
                            background: 'none', border: 'none',
                            cursor: (a.comment_count || 0) > 0 ? 'not-allowed' : 'pointer',
                            padding: '2px 6px',
                            borderRadius: 6,
                            color: (a.comment_count || 0) > 0 ? '#d1d5db' : 'var(--rh-text-muted)',
                            fontSize: 16, lineHeight: 1,
                            transition: 'color 0.15s',
                            opacity: (a.comment_count || 0) > 0 ? 0.5 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if ((a.comment_count || 0) === 0) e.currentTarget.style.color = '#dc2626';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = (a.comment_count || 0) > 0 ? '#d1d5db' : 'var(--rh-text-muted)';
                          }}
                        >
                          ×
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add account */}
            <div className="rh-form-group" style={{ marginBottom: 20 }}>
              <label>Add Reddit Account</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  placeholder="username (without u/)"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAccount())}
                  style={{ flex: 1 }}
                />
                <button
                  className="rh-btn-primary"
                  style={{ flexShrink: 0 }}
                  onClick={addAccount}
                  disabled={qc.phase === 'checking' || !newUsername.trim()}
                >
                  {qc.phase === 'checking' ? 'Checking…' : 'Check'}
                </button>
              </div>
              {qc.error && <p className="rh-input-error">{qc.error}</p>}
            </div>
          </div>

          {/* ── Right column: quality-check results ── */}
          {hasPanel && (
            <div style={{ flex: '1 1 0', minWidth: 0, alignSelf: 'stretch' }}>
              <AccountQualityResult
                result={qc.result}
                adding={adding}
                onAddAccount={() => saveAccount({ force: false })}
                onForceAdd={() => saveAccount({ force: true })}
                onRetry={qc.reset}
              />
            </div>
          )}
        </div>

        <div className="rh-modal-actions">
          <button className="rh-btn-secondary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Deactivate / Delete Modal ─────────────────────────────────────── */
function RemoveModal({ member, onClose, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleRemove(mode) {
    setBusy(true);
    setError('');
    try {
      await redditApi.delete(`/admin/redditors/${member.id}/?mode=${mode}`);
      onRemoved(
        mode === 'keep_data'
          ? `${member.full_name} has been deactivated. Their history is still saved.`
          : `${member.full_name} and all related data have been permanently deleted.`
      );
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  }

  const isStaff = member.is_admin || member.is_accountant;
  const assigned = member.total_assigned || 0;
  const submitted = member.total_submitted || 0;

  return (
    <div className="rh-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal" style={{ maxWidth: 480 }}>
        <h3 className="rh-modal-title">Deactivate {member.full_name}?</h3>
        <p style={{ fontSize: 13, color: 'var(--rh-text-muted)', margin: '-4px 0 18px', lineHeight: 1.5 }}>
          {isStaff
            ? 'They will lose login access. You can reactivate them later.'
            : 'Pick how you want to handle their account and history.'}
        </p>

        {/* Member summary */}
        <div style={{
          background: 'var(--rh-bg)', borderRadius: 10, padding: '12px 16px',
          marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: '50%', background: 'var(--rh-orange-light)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, color: 'var(--rh-orange)', flexShrink: 0,
          }}>
            {(member.full_name || '?')[0].toUpperCase()}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{member.full_name}</div>
            <div style={{ fontSize: 12, color: 'var(--rh-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {member.email}
            </div>
          </div>
          {!isStaff && (
            <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--rh-text-muted)', flexShrink: 0, lineHeight: 1.45 }}>
              <div><strong style={{ color: 'var(--rh-text)' }}>{assigned}</strong> assigned</div>
              <div><strong style={{ color: '#16a34a' }}>{submitted}</strong> submitted</div>
            </div>
          )}
        </div>

        {error && <div className="rh-auth-error" style={{ marginBottom: 12 }}>{error}</div>}

        {isStaff ? (
          <>
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10,
              padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <span style={{
                width: 32, height: 32, borderRadius: 8, background: '#dcfce7',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1,
              }}>
                <svg width="16" height="16" fill="none" stroke="#16a34a" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="9 12 11 14 15 10" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#15803d', marginBottom: 2 }}>History stays intact</div>
                <div style={{ fontSize: 12, color: '#166534', lineHeight: 1.5 }}>
                  Payments and activity they recorded will not be deleted. Only login access is turned off.
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="rh-btn-secondary" style={{ flex: 1 }} onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button
                className="rh-btn-danger"
                style={{ flex: 1 }}
                onClick={() => handleRemove('keep_data')}
                disabled={busy}
              >
                {busy ? 'Deactivating…' : 'Deactivate'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Recommended: soft deactivate */}
            <button
              disabled={busy}
              onClick={() => handleRemove('keep_data')}
              style={{
                width: '100%', textAlign: 'left', background: '#fffbeb',
                border: '1.5px solid #f59e0b', borderRadius: 10,
                padding: '14px 16px', marginBottom: 10, cursor: busy ? 'not-allowed' : 'pointer',
                transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
                opacity: busy ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (busy) return;
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245,158,11,0.15)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{
                  width: 36, height: 36, borderRadius: 8, background: '#fef3c7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="18" height="18" fill="none" stroke="#d97706" strokeWidth="1.8" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M10 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>Deactivate and keep history</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fde68a',
                      padding: '2px 7px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.03em',
                    }}>
                      Recommended
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--rh-text-muted)', lineHeight: 1.5 }}>
                    Turns off login and stops new comment assignments. Comments, payments, and past work stay saved so you can reactivate later.
                  </div>
                </div>
              </div>
            </button>

            {/* Permanent delete */}
            <button
              disabled={busy}
              onClick={() => handleRemove('delete_all')}
              style={{
                width: '100%', textAlign: 'left', background: '#fff',
                border: '1.5px solid var(--rh-border)', borderRadius: 10,
                padding: '14px 16px', marginBottom: 16, cursor: busy ? 'not-allowed' : 'pointer',
                transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
                opacity: busy ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (busy) return;
                e.currentTarget.style.borderColor = '#ef4444';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239,68,68,0.1)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--rh-border)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{
                  width: 36, height: 36, borderRadius: 8, background: '#fee2e2',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="18" height="18" fill="none" stroke="#dc2626" strokeWidth="1.8" viewBox="0 0 24 24">
                    <polyline points="3 6 5 6 21 6" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 11v6M14 11v6" strokeLinecap="round" />
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626', marginBottom: 3 }}>
                    Delete permanently
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--rh-text-muted)', lineHeight: 1.5 }}>
                    Removes this account
                    {assigned > 0 ? `, ${assigned} comment${assigned !== 1 ? 's' : ''},` : ''}
                    {' '}and payment records. This cannot be undone.
                  </div>
                </div>
              </div>
            </button>

            <button className="rh-btn-secondary" style={{ width: '100%' }} onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Edit Modal ────────────────────────────────────────────────────── */
function EditModal({ member, onClose, onSave }) {
  const isRedditor = !member.is_admin && !member.is_accountant;
  const [form, setForm] = useState({
    first_name: member.full_name.split(' ')[0] || '',
    last_name: member.full_name.split(' ').slice(1).join(' ') || '',
    is_admin: member.is_admin,
    rate_per_comment: member.rate_per_comment ?? '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        is_admin: form.is_admin,
      };
      if (isRedditor) payload.rate_per_comment = parseFloat(form.rate_per_comment) || 0;
      await redditApi.patch(`/admin/redditors/${member.id}/`, payload);
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal">
        <h3 className="rh-modal-title">Edit Member</h3>
        <p className="rh-modal-subtitle">{member.email}</p>
        {error && <div className="rh-auth-error">{error}</div>}
        <form onSubmit={onSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="rh-form-group">
              <label>First name</label>
              <input required value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
            </div>
            <div className="rh-form-group">
              <label>Last name</label>
              <input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
            </div>
          </div>

          {isRedditor && (
            <div className="rh-form-group">
              <label>Rate per submitted comment ($)</label>
              <input
                type="number"
                placeholder="e.g. 3.00"
                min="0"
                step="0.01"
                value={form.rate_per_comment}
                onChange={(e) => set('rate_per_comment', e.target.value)}
              />
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--rh-text-muted)' }}>
                USD paid per successfully submitted (non-deleted) comment each month.
              </p>
            </div>
          )}

          {!member.is_protected && !isRedditor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 16px' }}>
              <input
                type="checkbox"
                id="edit_is_admin"
                checked={form.is_admin}
                onChange={(e) => set('is_admin', e.target.checked)}
              />
              <label htmlFor="edit_is_admin" style={{ margin: 0, fontSize: 13 }}>Admin access</label>
            </div>
          )}

          <div className="rh-modal-actions">
            <button type="button" className="rh-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="rh-btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Member row ────────────────────────────────────────────────────── */
function MemberRow({ member, onEdit, onDelete, onReactivate, onAccounts, showStats, reactivating }) {
  const roleBg = member.is_protected
    ? 'rgba(255,69,0,0.12)'
    : member.is_admin
    ? 'rgba(99,102,241,0.12)'
    : member.is_accountant
    ? 'rgba(37,99,235,0.10)'
    : 'rgba(0,0,0,0.06)';
  const roleColor = member.is_protected
    ? 'var(--rh-orange)'
    : member.is_admin
    ? '#6366f1'
    : member.is_accountant
    ? '#2563eb'
    : 'var(--rh-text-muted)';
  const isDeactivated = member.is_active === false;

  return (
    <tr style={isDeactivated ? { opacity: 0.72 } : undefined}>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', background: 'var(--rh-orange-light)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: 'var(--rh-orange)', flexShrink: 0,
          }}>
            {(member.full_name || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {member.full_name}
              {isDeactivated && (
                <span style={{
                  background: 'rgba(220,38,38,0.1)', color: '#dc2626',
                  padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                  Deactivated
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--rh-text-muted)' }}>{member.email}</div>
          </div>
        </div>
      </td>
      <td>
        <span style={{
          background: roleBg, color: roleColor,
          padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
          display: 'inline-flex', alignItems: 'center', gap: 5,
        }}>
          {member.is_protected && <LockIcon />}
          {member.display_role}
        </span>
      </td>
      {showStats && (
        <>
          <td style={{ color: 'var(--rh-text-muted)', fontSize: 13 }}>
            {member.rate_per_comment > 0 ? `$${parseFloat(member.rate_per_comment).toFixed(2)}` : '—'}
          </td>
          <td>{member.total_assigned}</td>
          <td style={{ color: '#16a34a', fontWeight: 500 }}>{member.total_submitted}</td>
          <td style={{ color: '#dc2626' }}>{member.total_deleted}</td>
        </>
      )}
      <td>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="rh-btn-secondary"
            style={{ padding: '5px 12px', fontSize: 12 }}
            onClick={() => onEdit(member)}
          >
            Edit
          </button>
          {onAccounts && !member.is_admin && !member.is_accountant && (
            <button
              className="rh-btn-secondary"
              style={{ padding: '5px 12px', fontSize: 12 }}
              onClick={() => onAccounts(member)}
              title="Manage Reddit accounts"
            >
              Accounts
            </button>
          )}
          {member.is_protected ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, color: 'var(--rh-text-faint)', padding: '5px 8px',
            }}>
              <LockIcon /> Protected
            </span>
          ) : isDeactivated ? (
            <button
              className="rh-btn-primary"
              style={{ padding: '5px 12px', fontSize: 12 }}
              onClick={() => onReactivate?.(member)}
              disabled={reactivating}
              title="Restore login and show in Active list"
            >
              {reactivating ? 'Reactivating…' : 'Reactivate'}
            </button>
          ) : (
            <button
              className="rh-btn-danger"
              style={{ padding: '5px 12px', fontSize: 12 }}
              onClick={() => onDelete(member)}
              title="Deactivate or permanently remove"
            >
              Deactivate
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ─── Main component ────────────────────────────────────────────────── */
export default function ManageRedditorsTab({ onRedditorChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active'); // active | deactivated | all
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [managingAccounts, setManagingAccounts] = useState(null);
  const [reactivatingId, setReactivatingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [inviteAction, setInviteAction] = useState({}); // { [id]: 'revoking' | 'resending' }
  const [availableClients, setAvailableClients] = useState([]);

  const loadInvites = useCallback(async () => {
    setLoadingInvites(true);
    try {
      const { data } = await redditApi.get('/admin/invites/');
      setPendingInvites(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  async function revokeInvite(inv) {
    setInviteAction((a) => ({ ...a, [inv.id]: 'revoking' }));
    try {
      await redditApi.delete(`/admin/invites/${inv.id}/`);
      setPendingInvites((prev) => prev.filter((i) => i.id !== inv.id));
      setToast({ message: `Invite to ${inv.email} revoked.`, type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Revoke failed.', type: 'error' });
    } finally {
      setInviteAction((a) => { const n = { ...a }; delete n[inv.id]; return n; });
    }
  }

  async function resendInvite(inv) {
    setInviteAction((a) => ({ ...a, [inv.id]: 'resending' }));
    try {
      await redditApi.post(`/admin/invites/${inv.id}/resend/`);
      setToast({ message: `Invite resent to ${inv.email}.`, type: 'success' });
      loadInvites();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Resend failed.', type: 'error' });
    } finally {
      setInviteAction((a) => { const n = { ...a }; delete n[inv.id]; return n; });
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await redditApi.get('/admin/redditors/', {
        params: { status: statusFilter },
      });
      setMembers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadInvites();
    redditApi.get('/admin/clients/').then(({ data }) => {
      const list = data?.clients || data || [];
      setAvailableClients(list.map((c) => c.client_name || c.name || c).filter(Boolean).sort());
    }).catch(() => {});
  }, [loadInvites]);

  function onRemoved(message) {
    setToast({ message, type: 'success' });
    load();
    onRedditorChange?.();
  }

  function onSaved() {
    setToast({ message: 'Member updated.', type: 'success' });
    load();
    onRedditorChange?.();
  }

  async function onReactivate(member) {
    setReactivatingId(member.id);
    try {
      await redditApi.patch(`/admin/redditors/${member.id}/`, { is_active: true });
      setToast({ message: `${member.full_name} reactivated.`, type: 'success' });
      load();
      onRedditorChange?.();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || 'Reactivate failed.', type: 'error' });
    } finally {
      setReactivatingId(null);
    }
  }

  const admins = members.filter((m) => m.is_admin);
  const accountants = members.filter((m) => !m.is_admin && m.is_accountant);
  const adminAndAccountants = [...admins, ...accountants];
  const redditors = members.filter((m) => !m.is_admin && !m.is_accountant);

  const emptyHint = statusFilter === 'deactivated'
    ? 'No deactivated members.'
    : statusFilter === 'all'
    ? 'No members yet.'
    : 'No active members.';

  // Header ≈ 44px, each row ≈ 52px. Fit to rows; scroll only after 9.
  const HEADER_H = 44;
  const ROW_H = 52;
  const VISIBLE_ROWS = 9;
  const TABLE_VIEWPORT_H = HEADER_H + VISIBLE_ROWS * ROW_H;

  function tableScrollStyle(rowCount) {
    if (rowCount <= VISIBLE_ROWS) return {};
    return { height: TABLE_VIEWPORT_H, overflowY: 'auto' };
  }

  const statusOptions = [
    { id: 'active', label: 'Active' },
    { id: 'deactivated', label: 'Deactivated' },
    { id: 'all', label: 'All' },
  ];

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
      <div className="rh-tab-scroll">

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 className="rh-page-title">Manage Members</h2>
          <p className="rh-page-subtitle">Invite and manage admins and Reddit commenters</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          <div style={{
            display: 'inline-flex', background: 'var(--rh-bg)', border: '1px solid var(--rh-border)',
            borderRadius: 9, padding: 3, gap: 2,
          }}>
            {statusOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setStatusFilter(opt.id)}
                style={{
                  padding: '6px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                  background: statusFilter === opt.id ? 'var(--rh-orange)' : 'transparent',
                  color: statusFilter === opt.id ? '#fff' : 'var(--rh-text-muted)',
                  transition: 'background 0.2s ease, color 0.2s ease',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button className="rh-btn-primary" onClick={() => setShowInvite(true)}>
            + Add Member
          </button>
        </div>
      </div>

      {/* ── Admins & Accountants (merged) ── */}
      <div className="rh-card">
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--rh-border)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--rh-text)' }}>Admins</span>
          <span style={{
            background: 'rgba(255,69,0,0.1)', color: 'var(--rh-orange)',
            borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600,
          }}>{admins.length}</span>
          <span style={{ fontSize: 12, color: 'var(--rh-text-faint)' }}>Full access</span>
          <span style={{ width: 1, height: 14, background: 'var(--rh-border)', margin: '0 4px' }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--rh-text)' }}>Accountants</span>
          <span style={{
            background: 'rgba(37,99,235,0.08)', color: '#2563eb',
            borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600,
          }}>{accountants.length}</span>
          <span style={{ fontSize: 12, color: 'var(--rh-text-faint)' }}>Payments access only</span>
        </div>
        <div className="rh-table-wrap rh-table-scroll" style={tableScrollStyle(adminAndAccountants.length)}>
          <table className="rh-table">
            <thead>
              <tr><th>Member</th><th>Role</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} className="rh-empty">Loading…</td></tr>
              ) : adminAndAccountants.length === 0 ? (
                <tr><td colSpan={3} className="rh-empty">{emptyHint}</td></tr>
              ) : adminAndAccountants.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  showStats={false}
                  onEdit={setEditing}
                  onDelete={setRemoving}
                  onReactivate={onReactivate}
                  reactivating={reactivatingId === m.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Redditors ── */}
      <div className="rh-card">
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--rh-border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--rh-text)' }}>Redditors</span>
          <span style={{
            background: 'rgba(0,0,0,0.06)', color: 'var(--rh-text-muted)',
            borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600,
          }}>{redditors.length}</span>
          <span style={{ fontSize: 12, color: 'var(--rh-text-faint)', marginLeft: 4 }}>Freelancers who post Reddit comments</span>
        </div>
        <div className="rh-table-wrap rh-table-scroll" style={tableScrollStyle(redditors.length)}>
          <table className="rh-table">
            <thead>
              <tr>
                <th>Redditor</th>
                <th>Role</th>
                <th>Rate/Comment</th>
                <th>Assigned</th>
                <th>Submitted</th>
                <th>Deleted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="rh-empty">Loading…</td></tr>
              ) : redditors.length === 0 ? (
                <tr><td colSpan={7} className="rh-empty">
                  {statusFilter === 'active'
                    ? 'No active redditors. Use "+ Add Member" to invite one.'
                    : emptyHint}
                </td></tr>
              ) : redditors.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  showStats={true}
                  onEdit={setEditing}
                  onDelete={setRemoving}
                  onReactivate={onReactivate}
                  onAccounts={setManagingAccounts}
                  reactivating={reactivatingId === m.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Pending Invites ── */}
      <div className="rh-card">
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--rh-border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--rh-text)' }}>Pending Invites</span>
          {!loadingInvites && (
            <span style={{
              background: 'rgba(0,0,0,0.06)', color: 'var(--rh-text-muted)',
              borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600,
            }}>{pendingInvites.length}</span>
          )}
          <span style={{ fontSize: 12, color: 'var(--rh-text-faint)', marginLeft: 4 }}>Invitations sent but not yet accepted</span>
          <button
            onClick={loadInvites}
            title="Refresh"
            style={{
              marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
              padding: 4, borderRadius: 6, color: 'var(--rh-text-faint)', display: 'inline-flex',
              alignItems: 'center', transition: 'color 0.15s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--rh-orange)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--rh-text-faint)'}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
        </div>
        <div className="rh-table-wrap">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Sent</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingInvites ? (
                <tr><td colSpan={6} className="rh-empty">Loading…</td></tr>
              ) : pendingInvites.length === 0 ? (
                <tr><td colSpan={6} className="rh-empty">No pending invites.</td></tr>
              ) : pendingInvites.map((inv) => {
                const sentDate = new Date(inv.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
                const expiresDate = new Date(inv.expires_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
                const busy = inviteAction[inv.id];
                const ROLE_META = {
                  admin:      { label: 'Admin',      bg: 'rgba(255,69,0,0.1)',   color: 'var(--rh-orange)' },
                  accountant: { label: 'Accountant', bg: 'rgba(37,99,235,0.08)', color: '#2563eb' },
                  redditor:   { label: 'Redditor',   bg: 'rgba(0,0,0,0.06)',     color: 'var(--rh-text-muted)' },
                };
                const roleMeta = ROLE_META[inv.role] || ROLE_META.redditor;
                return (
                  <tr key={inv.id}>
                    <td style={{ fontWeight: 500, color: 'var(--rh-text)' }}>{inv.email}</td>
                    <td>
                      <span style={{
                        display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: roleMeta.bg, color: roleMeta.color,
                      }}>
                        {roleMeta.label}
                      </span>
                    </td>
                    <td className="rh-text-sm rh-text-muted" style={{ whiteSpace: 'nowrap' }}>{sentDate}</td>
                    <td className="rh-text-sm rh-text-muted" style={{ whiteSpace: 'nowrap' }}>{expiresDate}</td>
                    <td>
                      {inv.is_expired ? (
                        <span style={{
                          display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: '#fef2f2', color: '#dc2626',
                        }}>Expired</span>
                      ) : (
                        <span style={{
                          display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: 'rgba(34,197,94,0.1)', color: '#16a34a',
                        }}>Awaiting</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button
                          disabled={!!busy}
                          onClick={() => resendInvite(inv)}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer',
                            border: '1px solid var(--rh-border)', background: 'var(--rh-surface)',
                            color: busy === 'resending' ? 'var(--rh-text-faint)' : 'var(--rh-text-muted)',
                            opacity: busy ? 0.6 : 1, transition: 'all 0.15s',
                          }}
                        >
                          {busy === 'resending' ? 'Sending…' : 'Resend'}
                        </button>
                        <button
                          disabled={!!busy}
                          onClick={() => revokeInvite(inv)}
                          style={{
                            fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer',
                            border: '1px solid #fca5a5', background: '#fef2f2',
                            color: busy === 'revoking' ? '#fca5a5' : '#dc2626',
                            opacity: busy ? 0.6 : 1, transition: 'all 0.15s',
                          }}
                        >
                          {busy === 'revoking' ? 'Revoking…' : 'Revoke'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showInvite && (
        <InviteModal
          hasAccountant={accountants.length > 0}
          availableClients={availableClients}
          onClose={() => setShowInvite(false)}
          onSent={(email) => {
            setToast({ message: `Invite sent to ${email}.`, type: 'success' });
            loadInvites();
          }}
        />
      )}
      {editing && (
        <EditModal member={editing} onClose={() => setEditing(null)} onSave={onSaved} />
      )}
      {removing && (
        <RemoveModal member={removing} onClose={() => setRemoving(null)} onRemoved={onRemoved} />
      )}
      {managingAccounts && (
        <AccountsModal member={managingAccounts} onClose={() => setManagingAccounts(null)} />
      )}
      </div>{/* end rh-tab-scroll */}
    </>
  );
}
